import React, { useState } from "react";
import { Upload, Button, Spin, message } from "antd";
import { CloudUploadOutlined, PaperClipOutlined, CloseOutlined } from "@ant-design/icons";
import Service from "../../service";

const ACCEPT = ".pdf,.doc,.docx";

/**
 * Uploads the resume to the server as soon as it is chosen so the application
 * can reference it. `value` is { resumePath, filename }.
 */
const ResumeUploadField = ({ companyslug, publicSlug, maxSizeMb, value, onChange }) => {
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    const extension = `.${(file.name.split(".").pop() || "").toLowerCase()}`;
    if (!ACCEPT.split(",").includes(extension)) {
      message.error("Please upload a PDF, DOC or DOCX file.");
      return false;
    }
    if (file.size > maxSizeMb * 1024 * 1024) {
      message.error(`Resume must be smaller than ${maxSizeMb} MB.`);
      return false;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("resume", file);
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.publicResumeParse(companyslug, publicSlug),
        body: formData,
      });
      if (response?.data?.status === 200 && response.data.data?.resume_path) {
        onChange({ resumePath: response.data.data.resume_path, filename: file.name });
      } else {
        message.error(response?.data?.message || "Could not upload the resume. Please try again.");
      }
    } catch (error) {
      message.error("Could not upload the resume. Please try again.");
    } finally {
      setUploading(false);
    }
    return false;
  };

  if (value?.resumePath) {
    return (
      <div className="career-resume-selected">
        <PaperClipOutlined />
        <span className="career-resume-name" title={value.filename}>
          {value.filename}
        </span>
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => onChange(null)} aria-label="Remove resume" />
      </div>
    );
  }

  return (
    <Upload.Dragger
      className="career-resume-dropzone"
      beforeUpload={upload}
      showUploadList={false}
      accept={ACCEPT}
      maxCount={1}
      multiple={false}
      disabled={uploading}
    >
      {uploading ? (
        <div className="career-resume-reading">
          <Spin /> Uploading your resume...
        </div>
      ) : (
        <>
          <p className="career-resume-dropzone-icon">
            <CloudUploadOutlined />
          </p>
          <p className="career-resume-dropzone-title">
            Upload resume <span className="career-required-mark">*</span>
          </p>
          <p className="career-resume-dropzone-hint">Click or drag a file here. PDF, DOC or DOCX up to {maxSizeMb} MB.</p>
        </>
      )}
    </Upload.Dragger>
  );
};

export default ResumeUploadField;
